import { styled } from 'styled-components';
import { ChangeEvent, ComponentProps, useMemo, useState } from 'react';

import {
  Assets,
  Container,
  ContainerProps,
  Feedback,
  Flex,
  FlexProps,
  ForaModal,
  Headings,
  InputField,
  RadioButton,
  Text,
  theme,
  useMediaMin,
} from '@foradevelopment/fora-ui';

import { InactiveEmployment, InactiveEmploymentsModalContentProps, InactiveEmploymentsModalProps } from './types';
import { filterInactiveEmploymentListIndices } from './filterInactiveEmploymentListIndices';
import { formatEmployeeId } from '../../../common/utils';
import { useTypedTranslation } from '../../../i18n/i18n';

export const InactiveEmploymentsModal = ({
  isOpen,
  closeModal,
  confirmSelection,
  ...rest
}: { isOpen: boolean } & InactiveEmploymentsModalProps) => {
  const [showError, setShowError] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState<InactiveEmployment>();
  const { t } = useTypedTranslation();
  const onAddClick = () => {
    if (selectedEmployment) {
      confirmSelection(selectedEmployment.policyNumber);
    } else {
      setShowError(true);
    }
  };
  const onSelection = (employment: InactiveEmployment) => {
    setShowError(false);
    setSelectedEmployment(employment);
  };

  return (
    <ForaModal
      isOpen={isOpen}
      closeModal={closeModal}
      label={t('panel.terminateEmployment.addInactiveEmployeeHeading')}
    >
      {/* A set height is important, so that only the result list can be scrollable */}
      <ForaModal.Content hasCloseButton closeModal={closeModal} style={{ height: `calc(100vh - 12.5rem)` }}>
        <ModalContent
          onSelection={onSelection}
          selectedEmployment={selectedEmployment}
          showError={showError}
          {...rest}
        />
      </ForaModal.Content>
      <ForaModal.Footer
        onCancelClick={closeModal}
        onActionClick={onAddClick}
        actionButtonText={t('panel.terminateEmployment.addLabel')}
      />
    </ForaModal>
  );
};

const ModalContent = ({
  employmentList,
  showError,
  onSelection,
  selectedEmployment,
  loading,
}: InactiveEmploymentsModalContentProps) => {
  const [filterInput, setFilterInput] = useState('');
  const { t } = useTypedTranslation();

  const { numberOfHits, filteredEmploymentsIndices } = useMemo(() => {
    const filteredEmploymentsIndices = filterInactiveEmploymentListIndices({
      employmentList,
      searchQuery: filterInput,
    });
    return { numberOfHits: filteredEmploymentsIndices.length, filteredEmploymentsIndices };
  }, [employmentList, filterInput]);

  return (
    // A set height is important, so that only the result list can be scrollable
    <Flex flexDirection="column" height="100%">
      <Container>
        <Headings.H3>{t('panel.terminateEmployment.addInactiveEmployeeHeading')}</Headings.H3>
        <Text marginTop={20} marginBottom={40}>
          {t('panel.terminateEmployment.employeeSearchText')}
        </Text>
        <InputField
          id={'filter-input'}
          value={filterInput}
          label={t('panel.terminateEmployment.inactiveEmployeeSearchInputText')}
          errors={[]}
          isActive={false}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
        />
        <Text marginTop={8} color="black80" style={{ visibility: filterInput ? 'visible' : 'hidden' }}>
          {t('common.hitsCounter', { count: numberOfHits })}
        </Text>
      </Container>

      <SearchResultList
        employmentListIndices={filteredEmploymentsIndices}
        employmentList={employmentList}
        onSelection={onSelection}
        selectedEmployment={selectedEmployment}
        loading={loading}
      />
      {showError && <Feedback type="error">{t('panel.terminateEmployment.feedbackText')}</Feedback>}
    </Flex>
  );
};

const SearchResultList = ({
  employmentListIndices,
  employmentList,
  selectedEmployment,
  onSelection,
  loading,
}: {
  employmentListIndices: number[];
  employmentList: InactiveEmployment[];
  selectedEmployment?: InactiveEmployment;
  onSelection: (emp: InactiveEmployment) => void;
  loading?: boolean;
}) => {
  const isTablet = useMediaMin(theme.breakpoint.md);
  const { t } = useTypedTranslation();

  return (
    // It's important that there is no wrapping element around ScrollableFlexContainer, so that only the list is scrollable
    <>
      <Container {...listMargins}>
        <HeadRow showIdColumn={isTablet} />
      </Container>
      <ScrollableFlexContainer {...listMargins}>
        {loading ? (
          <Loading />
        ) : employmentList.length === 0 ? (
          <Text marginTop={20} marginRight="auto" marginLeft="auto">
            {t('panel.terminateEmployment.noInactiveEmploymentsText')}
          </Text>
        ) : (
          employmentListIndices.map(i => (
            <SelectableEmployeeRow
              key={i}
              index={i}
              employment={employmentList[i]}
              onSelection={onSelection}
              isChecked={selectedEmployment?.policyNumber === employmentList[i].policyNumber}
              showIdColumn={isTablet}
            />
          ))
        )}
      </ScrollableFlexContainer>
    </>
  );
};

const SelectableEmployeeRow = ({
  employment,
  index,
  isChecked,
  onSelection,
  showIdColumn,
}: {
  employment: InactiveEmployment;
  index: number;
  isChecked: boolean;
  onSelection: (emp: InactiveEmployment) => void;
  showIdColumn: boolean;
}) => (
  <HoverableRadioLabel htmlFor={`radio-${index}`} isChecked={isChecked}>
    <Flex {...listRowProps}>
      <Container {...nameColumnProps} {...titleItemPaddingVert}>
        <RadioButton
          id={`radio-${index}`}
          name="radio-inactive-employment"
          value={`radio-${index}`}
          label={`${employment.employee.firstName} ${employment.employee.lastName}`}
          checked={isChecked}
          onChange={() => onSelection(employment)}
        />
      </Container>
      {showIdColumn && (
        <Container {...idColumnProps} {...titleItemPaddingVert}>
          <Text color="black90" whiteSpace="nowrap">
            {formatEmployeeId(employment.employee.employeeId)}
          </Text>
        </Container>
      )}
      <Container {...dateColumnProps} {...titleItemPaddingVert}>
        <Text color="black90" whiteSpace="nowrap">
          {employment.employmentEndDate}
        </Text>
      </Container>
    </Flex>
  </HoverableRadioLabel>
);

const HeadRow = ({ showIdColumn }: { showIdColumn: boolean }) => {
  const { t } = useTypedTranslation();
  return (
    <Flex {...titleRowProps}>
      <Container {...nameColumnProps}>
        <Text {...titleTextProps}>{t('panel.terminateEmployment.nameRowTitle')}</Text>
      </Container>
      {showIdColumn && (
        <Container {...idColumnProps}>
          <Text {...titleTextProps}>{t('panel.terminateEmployment.idRowTitle')}</Text>
        </Container>
      )}
      <Container {...dateColumnProps}>
        <Text {...titleTextProps}>{t('panel.terminateEmployment.endedRowTitle')}</Text>
      </Container>
    </Flex>
  );
};

const Loading = () => <Assets.Loading width={100} color="black20" marginTop={20} />;

const listMargins = { marginLeft: [-20, -40], marginRight: [-20, -40] };

const row = { width: '100%', paddingLeft: 24, paddingRight: 24 };
const listRowProps = { ...row, paddingBottom: 7, paddingTop: 7, alignItems: 'center' } as FlexProps;
const titleRowProps = {
  ...row,
  paddingBottom: 10,
  style: { borderBottom: `2px solid ${theme.colors.black30}` },
} as FlexProps;

const titleItemPaddingVert = { paddingTop: 17, paddingBottom: 17 };
const titleTextProps = { size: 'small', color: 'black70', fontWeight: 'semibold' } as ComponentProps<typeof Text>;

const itemPaddingSides = { paddingLeft: 16, paddingRight: 16 };
const nameColumnProps = { ...itemPaddingSides, flexGrow: 1 } as ContainerProps;
const idColumnProps = { ...itemPaddingSides, flexBasis: ['auto', 'auto', 130], flexShrink: 0 } as ContainerProps;
const dateColumnProps = { ...itemPaddingSides, flexBasis: ['auto', 'auto', 90], flexShrink: 0 } as ContainerProps;

const ScrollableFlexContainer = styled(Flex)`
  overflow-y: auto;
  flex-direction: column;
  flex-grow: 1;
`;

const HoverableRadioLabel = styled.label<{ isChecked: boolean }>`
  display: flex;
  &:hover {
    background-color: ${props => (props.isChecked ? theme.colors.babyGreen : theme.colors.babyGreenHover)};
  }
  background-color: ${props => (props.isChecked ? theme.colors.babyGreen : theme.colors.white)};
  cursor: pointer;
  &:not(:first-of-type) {
    border-top: 1px solid ${theme.colors.black10};
  }
`;
